import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { taxFilingsGateway } from '../index';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { sandboxTransmissionActionsEnabled } from '../../../shared/commercialActions';
import { useIdentity } from '../../identity/state/useIdentity';
import { LegacyTaxFilingsPage } from './LegacyTaxFilingsPage';
import { ComingLaterPage } from '../../../app/ComingLaterPage';
import {
  TaxFormCode,
  taxFormCodeLabels,
  taxFilingDashboardStatusLabels,
  UNAVAILABLE_ANNUAL_FORM_CARDS,
  type AnnualFormResponse,
  type QuarterlyFormResponse,
  type TaxFilingDashboardCardResponse,
} from '../types';

const TABS = [
  { key: 'quarterly', label: 'Quarterly Forms' },
  { key: 'annual', label: 'Annual Forms' },
  { key: 'contractor', label: 'Contractor Forms' },
] as const;
type Tab = (typeof TABS)[number]['key'];

function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: 'UTC' });
}

function periodLabel(card: TaxFilingDashboardCardResponse): string {
  return card.quarter ? `${card.taxYear} Q${card.quarter}` : `${card.taxYear}`;
}

/**
 * EPN-98: no live filing provider exists, so the dashboard's Prepare / Approve / Deny controls and its filed statuses would only ever show a
 * simulated "Accepted". A production bundle therefore keeps the existing "unavailable" page (no filing action, nothing fetched, document
 * generation still usable) and only development and test builds show the sandbox dashboard. See shared/commercialActions.ts.
 */
export function TaxFilingsPage() {
  return sandboxTransmissionActionsEnabled ? <EFilingDashboard /> : <LegacyTaxFilingsPage />;
}

function EFilingDashboard() {
  const { me } = useIdentity();
  const [tab, setTab] = useState<Tab>('quarterly');
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [cards, setCards] = useState<TaxFilingDashboardCardResponse[]>([]);
  const [quarterlyForms, setQuarterlyForms] = useState<QuarterlyFormResponse[]>([]);
  const [annualForms, setAnnualForms] = useState<AnnualFormResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [denyingKey, setDenyingKey] = useState<string | null>(null);
  const [denialReason, setDenialReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashboardCards, qforms, aforms] = await Promise.all([
        taxFilingsGateway.listDashboard(taxYear),
        taxFilingsGateway.listQuarterlyForms(),
        taxFilingsGateway.listAnnualForms(),
      ]);
      setCards(dashboardCards);
      setQuarterlyForms(qforms);
      setAnnualForms(aforms);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load the e-filing dashboard.'));
    } finally {
      setLoading(false);
    }
  }, [taxYear]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const cardKey = (c: { formCode: number; quarter: number | null }) => `${c.formCode}-${c.quarter ?? 'annual'}`;

  async function handlePrepare(card: TaxFilingDashboardCardResponse) {
    setBusyKey(cardKey(card));
    setError(null);
    try {
      await taxFilingsGateway.submitFiling({ formCode: card.formCode, taxYear: card.taxYear, quarter: card.quarter });
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not prepare the filing.'));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleApprove(card: TaxFilingDashboardCardResponse) {
    if (!card.taxFilingId) return;
    setBusyKey(cardKey(card));
    setError(null);
    try {
      await taxFilingsGateway.approveFiling(card.taxFilingId);
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not approve the filing.'));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleDeny(e: FormEvent, card: TaxFilingDashboardCardResponse) {
    e.preventDefault();
    if (!card.taxFilingId) return;
    setBusyKey(cardKey(card));
    setError(null);
    try {
      await taxFilingsGateway.denyFiling(card.taxFilingId, denialReason);
      setDenyingKey(null);
      setDenialReason('');
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not deny the filing.'));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleGenerateForm(card: TaxFilingDashboardCardResponse) {
    setBusyKey(cardKey(card));
    setError(null);
    try {
      const liability = await taxFilingsGateway.calculateLiability({ formCode: card.formCode, taxYear: card.taxYear, quarter: card.quarter });
      if (card.formCode === TaxFormCode.Form941) {
        await taxFilingsGateway.generateQuarterlyForm(liability.id, card.taxFilingId);
      } else {
        await taxFilingsGateway.generateAnnualForm(liability.id, card.taxFilingId);
      }
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not generate the form.'));
    } finally {
      setBusyKey(null);
    }
  }

  const tiles = useMemo(() => ({
    notPrepared: cards.filter((c) => c.status === 'NotPrepared').length,
    inReview: cards.filter((c) => c.status === 'InReview').length,
    filed: cards.filter((c) => c.status === 'Filed').length,
    needsAttention: cards.filter((c) => c.status === 'NeedsAttention').length,
  }), [cards]);

  const quarterlyCards = cards.filter((c) => c.formCode === TaxFormCode.Form941).sort((a, b) => (a.quarter ?? 0) - (b.quarter ?? 0));
  const annualCard = cards.find((c) => c.formCode === TaxFormCode.Form940) ?? null;

  function renderRealCard(card: TaxFilingDashboardCardResponse) {
    const key = cardKey(card);
    const busy = busyKey === key;
    const generatedForm = card.formCode === TaxFormCode.Form941
      ? quarterlyForms.find((f) => f.taxYear === card.taxYear && f.quarter === card.quarter)
      : annualForms.find((f) => f.taxYear === card.taxYear);
    const isOwnFiling = !!card.preparedByEmailSnapshot && me?.email === card.preparedByEmailSnapshot;

    return (
      <div className="card" key={key} data-testid={`filing-card-${key}`}>
        <div className="section-header">
          <h3>{taxFormCodeLabels[card.formCode]} — {periodLabel(card)}</h3>
          <span className={`badge badge--status-${card.status.toLowerCase()}`}>{taxFilingDashboardStatusLabels[card.status]}</span>
        </div>
        <p className="muted">Due Date: {formatDate(card.dueDate)}</p>

        {card.status === 'NotPrepared' && (
          <button type="button" disabled={busy} onClick={() => void handlePrepare(card)}>
            {busy ? 'Preparing…' : 'Prepare Filing'}
          </button>
        )}

        {card.status === 'InReview' && (
          <>
            <p className="muted">Prepared by {card.preparedByEmailSnapshot}</p>
            {isOwnFiling ? (
              <p className="muted">Awaiting a different reviewer — you prepared this one.</p>
            ) : (
              <>
                <button type="button" disabled={busy} onClick={() => void handleApprove(card)}>Approve</button>
                <button type="button" disabled={busy} onClick={() => setDenyingKey(key)}>Deny</button>
              </>
            )}
            {denyingKey === key && (
              <form className="inline-form" onSubmit={(e) => void handleDeny(e, card)}>
                <label>
                  Denial reason
                  <input value={denialReason} onChange={(e) => setDenialReason(e.target.value)} required />
                </label>
                <button type="submit" disabled={busy}>Confirm deny</button>
                <button type="button" onClick={() => setDenyingKey(null)}>Cancel</button>
              </form>
            )}
          </>
        )}

        {card.status === 'Filed' && (
          <>
            <p className="muted">Accepted — submission ref: {card.providerSubmissionId}</p>
            <button type="button" disabled={busy} onClick={() => void handleGenerateForm(card)}>
              {busy ? 'Generating…' : generatedForm ? 'Regenerate Form' : 'Generate Form'}
            </button>
            {generatedForm && (
              <dl className="detail-list">
                <dt>Wages</dt><dd>${generatedForm.totalWages.toFixed(2)}</dd>
                <dt>Tax Withheld</dt><dd>${generatedForm.totalTaxWithheld.toFixed(2)}</dd>
                <dt>Deposits</dt><dd>${generatedForm.totalDeposits.toFixed(2)}</dd>
                <dt>Balance Due</dt><dd>${generatedForm.balanceDue.toFixed(2)}</dd>
              </dl>
            )}
          </>
        )}

        {card.status === 'NeedsAttention' && (
          <p className="error">
            {card.denialReason ? `Denied: ${card.denialReason}` : card.failureReason ? `Rejected: ${card.failureReason}` : 'Needs attention.'}
          </p>
        )}
      </div>
    );
  }

  function renderUnavailableCard(form: { code: string; name: string; description: string }) {
    return (
      <div className="card" key={form.code} data-testid={`filing-card-unavailable-${form.code}`}>
        <div className="section-header">
          <h3>{form.name}</h3>
          <span className="badge badge--status-notavailable">Not Available</span>
        </div>
        <p className="muted">{form.description}</p>
        <p className="muted">Not yet supported by EPN — no calculation or filing capability exists for this form.</p>
      </div>
    );
  }

  return (
    <section className="card card--wide">
      <div className="section-header">
        <h2>E-Filing Dashboard</h2>
        <span className="badge badge--caution">Sandbox Simulation Only — No Agency Submission</span>
      </div>
      <p className="muted">
        Filing submission is bookkeeping only (no dollar amounts, no agency contact) — see{' '}
        <code>docs/tax-filings.md</code> and <code>docs/epn-41-e-filing-dashboard-gaps.md</code>. A prepared
        filing must be approved or denied by a different user before it reaches the (Mock) provider; only
        Form 941 and Form 940 have real calculation/filing support today.
      </p>

      {error && <p className="error">{error}</p>}

      <div className="stat-grid" data-testid="filing-summary-tiles">
        <div className="stat-card"><span className="stat-value">{tiles.notPrepared}</span><span className="muted">Not Prepared</span></div>
        <div className="stat-card"><span className="stat-value">{tiles.inReview}</span><span className="muted">In Review</span></div>
        <div className="stat-card"><span className="stat-value">{tiles.filed}</span><span className="muted">Filed</span></div>
        <div className="stat-card"><span className="stat-value">{tiles.needsAttention}</span><span className="muted">Needs Attention</span></div>
      </div>

      <label>
        Tax Year
        <input
          data-testid="tax-year-input"
          type="number" min="2020" max="2100" value={taxYear}
          onChange={(e) => setTaxYear(Number(e.target.value))}
        />
      </label>

      <div className="tabs">
        {TABS.map((t) => (
          <button key={t.key} type="button" className={tab === t.key ? 'tab tab--active' : 'tab'} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {loading && <p className="muted">Loading…</p>}

      {!loading && tab === 'quarterly' && (
        <div className="card-grid" data-testid="quarterly-forms-panel">
          {quarterlyCards.map(renderRealCard)}
        </div>
      )}

      {!loading && tab === 'annual' && (
        <div className="card-grid" data-testid="annual-forms-panel">
          {annualCard && renderRealCard(annualCard)}
          {UNAVAILABLE_ANNUAL_FORM_CARDS.map(renderUnavailableCard)}
        </div>
      )}

      {tab === 'contractor' && <ComingLaterPage moduleName="Contractor Forms" />}
    </section>
  );
}
